/**
 * Precast Reinforcement Tool (BIM: Precast > Reinforcement)
 *
 * Rule-based automatic reinforcement for precast members. With a selection it
 * reinforces every precast panel/member at once; otherwise the next clicked
 * precast member receives the mesh. Bar diameter/spacing come from the
 * OptionsBar (falling back to the Configuration cover and mesh standards), and
 * the whole operation commits as one undoable command.
 */
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import { BIMElement, isPrecastCategory } from '../../core/types';
import { generatePrecastReinforcement } from '../../utils/precastGeometry';
import { getPrecastConfig } from '../../state/usePrecastConfigStore';
import i18n from '../../i18n';

export class PrecastReinforcementTool extends BaseTool {
    id = 'precast_reinforcement';
    name = 'Precast Reinforcement';
    cursor = 'crosshair';

    private get wireDiameterMm(): number {
        return this.context?.bimStore.toolProperties.precastReinfWireDiameter ?? getPrecastConfig().rebarDiameterMm;
    }

    private get spacingMm(): number {
        return this.context?.bimStore.toolProperties.precastReinfSpacing ?? getPrecastConfig().rebarSpacingMm;
    }

    onActivate(): void {
        // BIM honors the current selection first: reinforce all precast
        // members already picked, then stay active for further clicks.
        if (!this.context) return;
        const bim = this.context.bimStore;
        const selected = bim.selection
            .map(id => bim.project.elements.get(id))
            .filter((el): el is BIMElement => !!el && isPrecastCategory(el.category) && el.category !== 'Precast Reinforcement');
        if (selected.length > 0) {
            this.reinforceMany(selected);
        } else {
            this.notify(i18n.t('tools.notify.reinforcementPickAPrecastMemberOr'), 'info');
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event, (el) =>
            isPrecastCategory(el.category) && el.category !== 'Precast Reinforcement');
        if (!hit) {
            this.notify(i18n.t('tools.notify.reinforcementPickAPrecastPanelOr'), 'warning');
            return;
        }
        this.reinforceMany([hit.element]);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const hit = this.findElementHitAtPoint(event, (el) =>
            isPrecastCategory(el.category) && el.category !== 'Precast Reinforcement');
        if (hit) {
            const p = hit.point;
            this.context.appStore.setToolPreview({
                type: 'point',
                position: new THREE.Vector3(p.x, p.y, p.z + 0.02),
                color: '#21a179',
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.context?.appStore.setToolPreview(null);
        }
        super.onKeyDown(event);
    }

    /** Generate + add reinforcement for each member, replacing prior meshes. */
    private reinforceMany(members: BIMElement[]): void {
        if (!this.context) return;
        const bim = this.context.bimStore;
        const options = { wireDiameterMm: this.wireDiameterMm, spacingMm: this.spacingMm };
        const toAdd: BIMElement[] = [];
        const toRemove: string[] = [];

        for (const member of members) {
            // Drop any existing mesh on this member so re-running updates it.
            bim.project.elements.forEach((el) => {
                if (el.category === 'Precast Reinforcement' && el.geometryData?.hostId === member.id) {
                    toRemove.push(el.id);
                }
            });
            const reinf = generatePrecastReinforcement(member, options);
            if (reinf) toAdd.push(reinf);
        }

        if (toAdd.length === 0) {
            this.notify(i18n.t('tools.notify.reinforcementNoMeshCouldBeGenerated'), 'warning');
            return;
        }

        bim.replaceElements(
            `Add precast reinforcement to ${toAdd.length} member${toAdd.length > 1 ? 's' : ''}`,
            toRemove,
            toAdd,
        );
        this.notify(i18n.t('tools.notify.precastReinforced', { count: toAdd.length, diameter: this.wireDiameterMm, spacing: this.spacingMm }), 'success');
    }
}
